import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { updateInstallmentMaturity } from 'services/requests/contract';
import { format, startOfDay } from 'date-fns';
import { setMask } from 'utils';
import { Formik, Form } from 'formik';
import * as yup from 'yup'
import { DatePicker } from '@mui/x-date-pickers';

function prepareRequestBody(installmentsToUpdate) {
    const installments = installmentsToUpdate.map(item => {        
        return {
            id: item.id,
            action: 'maturity',
            value: item.newMaturity
        };
    })
    return {
        updateInstallments: installments
    }
}

export default function EditInstallment({ open, onClose, installmentData, contractData, onSuccess }) {
    const validationSchema = yup.object({
        installments: yup.array().of(
            yup.object({
                id: yup.number().required(),
                index: yup.number().required(),
                maturity: yup.string().required(),
                valueToPay: yup.number(),
                value: yup.number(),
                status: yup.string().required(),
                newMaturity: yup.string()
                    .required('Data de vencimento é obrigatória')
            })
        )
    });

    const getInitialValues = () => {
        if (!installmentData || !open) {
            return { installments: [] };
        }

        const formattedInstallments = installmentData.map(installment => ({
            ...installment,
            newMaturity: installment.maturity ? format(startOfDay(new Date(installment.maturity)), 'yyyy-MM-dd') : '',
        }));

        return { installments: formattedInstallments };
    };

    const handleFormSubmit = async (values, actions) => {
        try {
            const installmentsToUpdate = values.installments.filter(inst => {
                const originalDate = format(startOfDay(new Date(inst.maturity)), 'yyyy-MM-dd');
                return inst.newMaturity !== originalDate;
            });


            if (installmentsToUpdate.length === 0) {
                return;
            }

            const body = prepareRequestBody(installmentsToUpdate);

            await updateInstallmentMaturity({ contractId: contractData.id, body });

            onSuccess?.();
            onClose();

        } catch (error) {
            console.error(error);
            if (actions && actions.setErrors) {
                actions.setErrors({ submit: error.message });
            }
        } finally {
            if (actions && actions.setSubmitting) {
                actions.setSubmitting(false);
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Editar Vencimento das Parcelas
            </DialogTitle>

            <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={handleFormSubmit}
            >
                {({ values, setFieldValue, isSubmitting, errors, touched, actions }) => (
                    <Form>
                        <DialogContent>
                            <Stack spacing={3}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Edite as datas de vencimento das parcelas pendentes
                                </Typography>

                                {values.installments
                                    .filter(inst => inst.status !== 'payed')
                                    .map((installment) => (
                                        <Card key={installment.id} variant="outlined">
                                            <CardContent>
                                                <Grid container spacing={1} alignItems="center">
                                                    <Grid item xs={12}>
                                                        <Typography variant="h6" component="div">
                                                            Parcela {installment.index}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography variant="subtitle2">
                                                            Vencimento: {format(new Date(installment.maturity), 'dd/MM/yyyy')}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography variant="body2">
                                                            {setMask('money', installment.valueToPay || installment.value)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4} sx={{ mt: 2 }}>
                                                        <DatePicker
                                                            label="Novo Vencimento"
                                                            value={installment.newMaturity}
                                                            onChange={(selectedMaturity) => {
                                                                const formattedDate = selectedMaturity ?
                                                                    format(startOfDay(new Date(selectedMaturity)), 'yyyy-MM-dd') : '';
                                                                const index = values.installments.findIndex(inst => inst.id === installment.id);
                                                                if (index !== -1) {
                                                                    setFieldValue(`installments.${index}.newMaturity`, formattedDate);
                                                                }
                                                            }}
                                                            inputFormat="DD/MM/YYYY"
                                                            renderInput={params => (
                                                                <TextField
                                                                    {...params}
                                                                    fullWidth
                                                                    error={touched[`installments.${values.installments.findIndex(inst => inst.id === installment.id)}.newMaturity`] &&
                                                                        Boolean(errors[`installments.${values.installments.findIndex(inst => inst.id === installment.id)}.newMaturity`])}
                                                                    helperText={touched[`installments.${values.installments.findIndex(inst => inst.id === installment.id)}.newMaturity`] &&
                                                                        errors[`installments.${values.installments.findIndex(inst => inst.id === installment.id)}.newMaturity`]}
                                                                />
                                                            )}
                                                            minDate={new Date()}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="inherit">
                                Cancelar
                            </Button>
                            <LoadingButton
                                type="button"
                                variant="contained"
                                color="primary"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                                onClick={() => handleFormSubmit(values, actions)}
                            >
                                Salvar Alterações
                            </LoadingButton>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}